import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { WorkingPage } from '../working-page/index.ts';

export type EmptyStateElement = 'div' | 'section';

export type EmptyStateProps<T extends EmptyStateElement = 'div'> = Omit<
  ComponentPropsWithRef<T>,
  'style' | 'title'
> & {
  as?: T;
  /* What is not there yet, said plainly. Rendered as the heading. */
  title: ReactNode;
  /* A lucide icon for a whole page that is empty; a card's small empty line goes without. */
  icon?: ReactNode;
  /* One sentence: what would put something here. */
  description?: ReactNode;
  /*
   * The way out. Optional on purpose — a placeholder for something that is
   * coming later has nothing to offer, and a disabled button lying about that
   * is worse than no button.
   */
  action?: ReactNode;
  /* `h2` when the card around it has no title of its own, `h3` when it has. */
  headingLevel?: 'h2' | 'h3';
  /* Something is being written here: a page filling in above the title says it is work, not a hang. */
  busy?: boolean;
};

/*
 * A designed nothing. An empty feed proves the tool knows less than the one it
 * replaces, so every empty surface says what is missing and, where there is
 * one, names the single move that fills it.
 *
 * Centred, unlike the rest of the page: it is the only thing in its container,
 * and left-aligning one short line under a wide card reads as a rendering
 * failure rather than a state.
 */
export function EmptyState<T extends EmptyStateElement = 'div'>({
  as,
  className,
  title,
  description,
  action,
  icon,
  headingLevel,
  busy,
  ...props
}: EmptyStateProps<T>) {
  /*
   * TS cannot type-check JSX against a union of tags (refs are invariant), so
   * the render site is cast; EmptyStateProps<T> checks the call site.
   */
  const Component = (as ?? 'div') as 'div';
  const Heading = headingLevel ?? 'h2';
  return (
    <Component
      data-slot='empty-state'
      className={cn(
        'flex flex-col items-center gap-1.5 px-5 py-7 text-center',
        className,
      )}
      {...(props as Omit<ComponentPropsWithRef<'div'>, 'style'>)}
    >
      {busy && <WorkingPage className='mb-3' />}
      {icon !== undefined && icon !== null && (
        <span
          data-slot='empty-state-icon'
          aria-hidden='true'
          className='mb-3 flex size-14 items-center justify-center rounded-full bg-accent-tint text-success [&_svg]:size-6'
        >
          {icon}
        </span>
      )}
      <Heading data-slot='empty-state-title' className='text-md font-bold text-ink'>
        {title}
      </Heading>
      {description !== undefined && description !== null && (
        <p
          data-slot='empty-state-description'
          className='max-w-85 text-base text-ink-muted'
        >
          {description}
        </p>
      )}
      {action !== undefined && action !== null && (
        <div data-slot='empty-state-action' className='mt-2.5'>
          {action}
        </div>
      )}
    </Component>
  );
}
